/**
 * Suggestions for resolving a blocked room during booking:
 * nearest free slots in the same room on the same day.
 * (Alternative free rooms come from getAvailableRoomsForSlot via RTK Query.)
 */
import {
  resolveRoomAvailability,
  getRoomAppointments,
} from "../firebase";
import { toIsraelDate } from "./dateUtils";
import { findFreeGaps } from "./roomAvailability";

// Search window for rooms with no defined hours (default-open)
const DEFAULT_OPEN_WINDOW = [{ start: "07:00", end: "22:00" }];

/**
 * Up to `limit` free slots for the room on the given day, nearest to the
 * requested start time. Returns [] when the day is closed (closed_day/holiday).
 *
 * @param {string} dateStr "YYYY-MM-DD"
 * @param {string} startTime "HH:MM" requested start
 *
 * Error Code: CK-3819
 */
export const suggestNearestFreeSlots = async ({
  clinicId,
  roomId,
  dateStr,
  startTime,
  durationMins,
  excludeAppointmentId = null,
  limit = 3,
}) => {
  try {
    const start = new Date(`${dateStr}T${startTime}`);
    if (isNaN(start.getTime())) return [];
    const end = new Date(start.getTime() + durationMins * 60000);

    const resolution = await resolveRoomAvailability(
      clinicId,
      roomId,
      start,
      end
    );

    // Day fully closed — no same-day suggestions
    if (
      !resolution.available &&
      (resolution.reason === "closed_day" || resolution.reason === "holiday")
    ) {
      return [];
    }

    const openWindows = resolution.slotsForDate?.length
      ? resolution.slotsForDate
      : DEFAULT_OPEN_WINDOW;

    const dayStart = new Date(`${dateStr}T00:00:00`);
    const dayEnd = new Date(`${dateStr}T23:59:59.999`);
    const bookings = await getRoomAppointments(clinicId, roomId, dayStart, dayEnd);

    // Busy times in Israel wall-clock so they line up with the resolver's
    // open windows (slot strings are Asia/Jerusalem wall time).
    const busy = (bookings || [])
      .filter(
        (apt) =>
          apt.id !== excludeAppointmentId &&
          apt.isDeleted !== true &&
          apt.status !== "cancelled"
      )
      .map((apt) => {
        const s = toIsraelDate(apt.startTime);
        const e = toIsraelDate(apt.endTime);
        return {
          startMins: s.getHours() * 60 + s.getMinutes(),
          endMins: e.getHours() * 60 + e.getMinutes(),
        };
      });

    const ilStart = toIsraelDate(start);

    return findFreeGaps({
      openWindows,
      busy,
      durationMins,
      preferredStartMins: ilStart.getHours() * 60 + ilStart.getMinutes(),
      limit,
    });
  } catch (error) {
    console.error("[CK-3819] Error suggesting free room slots:", {
      errorCode: "CK-3819",
      errorMessage: error.message,
      clinicId,
      roomId,
      timestamp: new Date().toISOString(),
    });
    return [];
  }
};
